"use client";
import React from "react";

import { Box, BoxSx } from "../../foundations";
import { useFocusControls } from "../../hooks";
import { useId } from "../../hooks/useId";
import { TextFieldProps } from "../TextField";
import { RadioGroupContext } from "./RadioGroup";

export interface RadioButtonProps
  extends Omit<TextFieldProps, "value" | "error" | "label"> {
  value: string | number;
  error?: boolean;
  label?: Exclude<React.ReactNode, undefined | null | boolean>;
  id?: string;
}

const sharedRadioButtonStyles: BoxSx = {
  display: "inline-flex",
  alignItems: "center",
  marginRight: "spacing-4",
  width: "spacing-6",
  height: "spacing-6",
  border: "2px solid",
  borderRadius: "radius-round",
  cursor: "pointer",
  ":focus-visible": {
    outline: "2px solid",
    outlineColor: "focus",
    outlineOffset: "2px"
  }
};

const buttonWrapper: BoxSx = {
  display: "flex",
  flexDirection: "column",
  width: "fit-content"
};

export const RadioButton: React.FC<RadioButtonProps> = (
  props: RadioButtonProps
) => {
  const { value, label, disabled, error, onBlur, id } = props;
  const base0Id = useId();
  const inputId = id ?? base0Id;

  const [, preventOutlineOnClick, handleFocus, handleBlur] =
    useFocusControls(onBlur);

  const radioContext = React.useContext(RadioGroupContext);

  const isChecked = React.useMemo(() => {
    return radioContext?.selectedValue === value;
  }, [radioContext?.selectedValue, value]);

  const isDisabled = React.useMemo(() => {
    return radioContext?.disabled || disabled;
  }, [disabled, radioContext?.disabled]);

  const isError = React.useMemo((): boolean => {
    return Boolean(radioContext?.error || error);
  }, [radioContext?.error, error]);

  const defaultRadioButtonStyles = React.useMemo<BoxSx>(() => {
    return {
      ...sharedRadioButtonStyles,
      borderColor: isChecked ? "input-surface-enabled" : "input-stroke-enabled",
      transition: "transition-color-medium",
      ":hover": {
        borderColor: isChecked ? "input-surface-hover" : "input-stroke-hover",
        ":after": {
          backgroundColor: "input-surface-hover"
        }
      },
      ":active": {
        borderColor: isChecked ? "input-surface-click" : "input-stroke-click",
        ":after": {
          backgroundColor: "input-surface-click"
        }
      },
      ":after": {
        content: `""`,
        marginLeft: "spacing-1",
        width: "spacing-4",
        height: "spacing-4",
        backgroundColor: "input-surface-enabled",
        visibility: isChecked ? "visible" : "hidden",
        borderRadius: "radius-round",
        transition: "transition-color-medium"
      }
    };
  }, [isChecked]);

  const disabledRadioButtonStyles = React.useMemo<BoxSx>(() => {
    return {
      ...sharedRadioButtonStyles,
      borderColor: "input-stroke-disabled",
      cursor: "not-allowed",
      ":after": {
        content: `""`,
        marginLeft: "spacing-1",
        width: "spacing-4",
        height: "spacing-4",
        backgroundColor: isChecked ? "input-surface-disabled" : undefined,
        borderRadius: "radius-round",
        transition: "transition-color-medium"
      }
    };
  }, [isChecked]);

  const errorRadioButtonStyles = React.useMemo<BoxSx>(() => {
    return {
      ...sharedRadioButtonStyles,
      borderColor: "input-stroke-error",
      "&:active": {
        backgroundColor: "backgroundNeutral02"
      },
      ":after": {
        content: `""`,
        marginLeft: "spacing-1",
        width: "spacing-4",
        height: "spacing-4",
        backgroundColor: isChecked ? "input-stroke-error" : undefined,
        borderRadius: "radius-round",
        transition: "transition-color-medium"
      }
    };
  }, [isChecked]);

  const radioButtonStyles = React.useMemo<BoxSx>(() => {
    if (isDisabled) {
      return disabledRadioButtonStyles;
    } else if (isError) {
      return errorRadioButtonStyles;
    } else {
      return defaultRadioButtonStyles;
    }
  }, [
    defaultRadioButtonStyles,
    disabledRadioButtonStyles,
    errorRadioButtonStyles,
    isDisabled,
    isError
  ]);

  const labelStyles = React.useMemo<BoxSx>(() => {
    return {
      display: "inline-flex",
      alignItems: "center",
      color: isDisabled ? "input-text-disabled" : "input-text",
      cursor: isDisabled ? "not-allowed" : "pointer",
      fontSize: "font-size-4",
      lineHeight: "leading-2",
      letterSpacing: "kerning-none",
      paddingY: "spacing-3"
    };
  }, [isDisabled]);

  return (
    <Box
      onMouseDown={preventOutlineOnClick}
      onMouseUp={preventOutlineOnClick}
      sx={buttonWrapper}
    >
      <Box
        as="label"
        htmlFor={inputId}
        id={`${inputId}-label`}
        sx={labelStyles}
      >
        <Box
          as="input"
          type="radio"
          id={inputId}
          value={value}
          checked={isChecked}
          onChange={radioContext?.onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          sx={radioButtonStyles}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        />
        {label}
      </Box>
    </Box>
  );
};
